import React, { useState, useEffect } from 'react';
import { FaPlay, FaPause, FaCheck, FaTimes, FaClock, FaTrophy } from 'react-icons/fa';
import { database, ref, get, set, onValue } from '../firebase'; // database
import { storage, storageRef, uploadBytes, getDownloadURL } from '../firebase'; // storage
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiX, FiShoppingCart, FiArrowRight, FiPlay } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaRupeeSign } from 'react-icons/fa';
import { FiCreditCard } from 'react-icons/fi';
import { FiUpload } from 'react-icons/fi';
import gpayImg from '../img/gpay.png';
import phonepeImg from '../img/phonepe.svg';

// Constants for Firebase paths
const FIREBASE_PATHS = {
  TASKS: 'videoplayer/tasks',
};

const Task = () => {
  const [showPopup, setShowPopup] = useState(true);
  const [showTasks, setShowTasks] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  // Add modal state and handlers
  const [modalImage, setModalImage] = useState(null);
  const openImageModal = (url) => setModalImage(url);
  const closeImageModal = () => setModalImage(null);

  // Deposit modal states
  const [depositModal, setDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositTaskId, setDepositTaskId] = useState(null);
  // Replace paymentNumbers state with paymentInfo and fetch using onValue
  const [paymentInfo, setPaymentInfo] = useState({ gpayNumber: '', phonepeNumber: '' });
  const [selectedPayment, setSelectedPayment] = useState('');
  const [showPaymentNumber, setShowPaymentNumber] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState('');
  const [depositSuccess, setDepositSuccess] = useState('');
  // Add screenshot state
  const [depositScreenshot, setDepositScreenshot] = useState(null);
  const [depositScreenshotPreview, setDepositScreenshotPreview] = useState('');

  // Fetch user from localStorage and tasks from Firebase
  useEffect(() => {
    const fetchUserAndTasks = async () => {
      try {
        // Retrieve user data from localStorage
        const userData = JSON.parse(localStorage.getItem('currentUser'));
        if (!userData || (!userData.mobile && !userData.id)) {
          setError('You must be logged in to view tasks.');
          toast.error('Please log in to view tasks.');
          navigate('/login');
          return;
        }

        // Use mobile or id as userId and sanitize it
        const mobile = (userData.mobile || userData.id).replace(/[^0-9]/g, '_');
        setUserId(mobile);

        // Fetch tasks for the user
        const tasksRef = ref(database, `${FIREBASE_PATHS.TASKS}/${mobile}`);
        const tasksSnapshot = await get(tasksRef);
        const tasksData = tasksSnapshot.exists()
          ? Object.entries(tasksSnapshot.val())
              .map(([id, task]) => ({
                taskId: parseInt(id),
                ...task,
              }))
              .sort((a, b) => a.taskId - b.taskId)
          : [];
        setTasks(tasksData);
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to fetch tasks. Please try again.');
        toast.error('Failed to fetch tasks.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndTasks();
  }, [navigate]);

  // Show popup on page load
  useEffect(() => {
    setShowPopup(true);
  }, []);

  // Fetch payment numbers from Firebase using onValue (real-time updates)
  useEffect(() => {
    if (depositModal) {
      const paymentRef = ref(database, 'videoplayer/SubscriptionPlans/payment');
      const unsubscribe = paymentRef && paymentRef.key ? onValue(paymentRef, (snapshot) => {
        const data = snapshot.val();
        setPaymentInfo({
          gpayNumber: data?.gpayNumber || '',
          phonepeNumber: data?.phonepeNumber || '',
        });
      }) : undefined;
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [depositModal]);

  const handleClose = () => {
    setShowPopup(false);
    navigate('/home');
  };

  const handleGoToTasks = () => {
    setShowPopup(false);
    setShowTasks(true);
  };

  // Handle starting a task and updating its status to working
  const handleStartTask = async (task) => {
    try {
      const taskRef = ref(database, `${FIREBASE_PATHS.TASKS}/${userId}/${task.taskId}`);
      await set(taskRef, {
        taskId: task.taskId,
        title: task.title,
        description: task.description,
        withdrawRequestId: task.withdrawRequestId || null,
        assignedAt: task.assignedAt,
        status: 'working',
        startedAt: new Date().toISOString(),
      });

      setTasks((prev) =>
        prev
          .map((t) =>
            t.taskId === task.taskId
              ? { ...t, status: 'working', startedAt: new Date().toISOString() }
              : t
          )
          .sort((a, b) => a.taskId - b.taskId)
      );

      toast.success(`Task ${task.taskId}: "${task.title}" started!`);
    } catch (err) {
      console.error('Error starting task:', err);
      toast.error('Failed to start task. Please try again.');
    }
  };

  // Handle completing a task and updating its status to completed
  const handleCompleteTask = async (task) => {
    try {
      const taskRef = ref(database, `${FIREBASE_PATHS.TASKS}/${userId}/${task.taskId}`);
      await set(taskRef, {
        taskId: task.taskId,
        title: task.title,
        description: task.description,
        withdrawRequestId: task.withdrawRequestId || null,
        assignedAt: task.assignedAt,
        status: 'completed',
        startedAt: task.startedAt,
        completedAt: new Date().toISOString(),
      });

      setTasks((prev) =>
        prev
          .map((t) =>
            t.taskId === task.taskId
              ? { ...t, status: 'completed', completedAt: new Date().toISOString() }
              : t
          )
          .sort((a, b) => a.taskId - b.taskId)
      );

      toast.success(`Task ${task.taskId}: "${task.title}" completed successfully!`);
    } catch (err) {
      console.error('Error completing task:', err);
      toast.error('Failed to complete task. Please try again.');
    }
  };

  // Open deposit modal for a specific task
  const handleOpenDeposit = () => {
    setDepositTaskId(null); // Not tied to a specific task
    setDepositAmount('');
    setSelectedPayment('');
    setShowPaymentNumber(false);
    setDepositError('');
    setDepositSuccess('');
    setDepositScreenshot(null);
    setDepositScreenshotPreview('');
    setDepositModal(true);
  };
  const handleCloseDeposit = () => {
    setDepositModal(false);
  };

  // Handle deposit screenshot change
  const handleDepositScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDepositScreenshot(file);
      setDepositScreenshotPreview(URL.createObjectURL(file));
    }
  };

  // Handle deposit submission
  const handleDepositSubmit = async () => {
    setDepositLoading(true);
    setDepositError('');
    setDepositSuccess('');
    try {
      if (!depositAmount || isNaN(depositAmount) || Number(depositAmount) <= 0) {
        setDepositError('Please enter a valid amount.');
      setDepositLoading(false);
      return;
    }
    if (!selectedPayment) {
      setDepositError('Please select a payment method.');
      setDepositLoading(false);
      return;
    }
    if (!depositScreenshot) {
      setDepositError('Please upload a payment screenshot.');
      setDepositLoading(false);
      return;
    }

    // Get user mobile from localStorage
    const userDataRaw = localStorage.getItem('currentUser');
    if (!userDataRaw) {
      setDepositError('User not logged in. Please log in again.');
      setDepositLoading(false);
      return;
    }
    let userData;
    try {
      userData = JSON.parse(userDataRaw);
    } catch (parseErr) {
      setDepositError('User data corrupted. Please log in again.');
      setDepositLoading(false);
      return;
    }
    const mobile = (userData.mobile || userData.id || '').replace(/[^0-9]/g, '_');
    if (!mobile) {
      setDepositError('User ID missing. Please log in again.');
      setDepositLoading(false);
      return;
    }

    // Store deposit in user's deposite node in sequence
    const depositRef = ref(database, `videoplayer/Users/${mobile}/deposite`);
    // Fetch current deposits to determine next key
    let nextKey = 1;
    try {
      const depositSnap = await get(depositRef);
      if (depositSnap.exists()) {
        const deposits = depositSnap.val();
        const keys = Object.keys(deposits).map(Number);
        nextKey = Math.max(...keys) + 1;
      }
    } catch (dbReadErr) {
      setDepositError('Failed to read deposit history. Please try again.');
      setDepositLoading(false);
      return;
    }

    // Upload screenshot to storage
    let screenshotUrl = '';
    try {
      const ext = depositScreenshot.name.split('.').pop() || 'png';
      const screenshotStorageRef = storageRef(storage, `videoplayer/Deposit/${mobile}/${nextKey}_deposit.${ext}`);
      await uploadBytes(screenshotStorageRef, depositScreenshot);
      screenshotUrl = await getDownloadURL(screenshotStorageRef);
    } catch (uploadErr) {
      console.error('Screenshot upload error:', uploadErr); // Log the real error
      setDepositError('There was a problem uploading your screenshot. Please check the console for details.');
      setDepositLoading(false);
      return;
    }

    // Store deposit data in database
    try {
      await set(ref(database, `videoplayer/Users/${mobile}/deposite/${nextKey}`), {
        amount: Number(depositAmount),
        method: selectedPayment,
        paymentNumber: selectedPayment === 'gpay' ? paymentInfo.gpayNumber : paymentInfo.phonepeNumber,
        screenshotUrl,
        timestamp: new Date().toISOString(),
      });
    } catch (dbWriteErr) {
      setDepositError('Failed to save deposit in database. Please try again.');
      setDepositLoading(false);
      return;
    }

    setDepositSuccess('Deposit recorded successfully!');
    setDepositAmount('');
    setSelectedPayment('');
    setShowPaymentNumber(false);
    setDepositScreenshot(null);
    setDepositScreenshotPreview('');
    setTimeout(() => setDepositModal(false), 1500);
  } catch (err) {
    setDepositError('Unexpected error. Please try again.');
    console.error('Deposit error:', err);
  } finally {
    setDepositLoading(false);
  }
};

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex flex-col items-center justify-center p-4">
      {/* Image Modal */}
      <AnimatePresence>
        {modalImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-2xl w-full mx-4 flex flex-col items-center">
              <button
                onClick={closeImageModal}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
                aria-label="Close modal"
              >
                <FiX className="text-2xl" />
              </button>
              <img
                src={modalImage}
                alt="Task Full"
                className="w-full h-auto max-h-[80vh] object-contain rounded"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Popup for Withdrawal Conditions */}
      <AnimatePresence>
        {showPopup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md border-2 border-amber-300 dark:border-amber-700 overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400"></div>
              <div className="absolute -top-10 -right-10 w-20 h-20 rounded-full bg-amber-400/10 blur-xl"></div>
              <div className="absolute -bottom-10 -left-10 w-20 h-20 rounded-full bg-amber-400/10 blur-xl"></div>

              <div className="relative z-10">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-amber-800 dark:text-amber-300 flex items-center">
                    <FaRupeeSign className="h-8 w-8 text-amber-600 dark:text-amber-400 mr-2 bg-amber-100 dark:bg-amber-900/30 p-1.5 rounded-full" />
                    Withdrawal Conditions
                  </h2>
                  <button
                    onClick={handleClose}
                    className="p-1.5 rounded-full hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-all duration-200 text-amber-600 dark:text-amber-400"
                    aria-label="Close popup"
                  >
                    <FiX className="h-5 w-5" />
                  </button>
                </div>

                <p className="text-gray-700 dark:text-gray-300 mb-6 text-sm leading-relaxed">
                  To make your first withdrawal, you need to complete at least one task each of <strong>Purchase</strong> and <strong>Sale</strong>. Start earning points now!
                </p>

                <div className="flex justify-between gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-2 px-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-300 hover:shadow-md flex items-center justify-center"
                  >
                    <FiX className="mr-2 h-4 w-4" />
                    Close
                  </button>
                  <button
                    onClick={handleGoToTasks}
                    className="flex-1 py-2 px-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white text-sm font-bold rounded-lg shadow-md transition-all duration-300 hover:shadow-lg flex items-center justify-center"
                  >
                    <FiArrowRight className="mr-2 h-4 w-4" />
                    Go to Tasks
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Task List Section */}
      <AnimatePresence>
        {showTasks && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6"
          >
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
              <FiCheckCircle className="h-6 w-6 text-amber-500 dark:text-amber-400 mr-2" />
              Your Tasks
            </h2>
            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 text-sm">{error}</div>
            )}
            <div className="w-full flex justify-center sm:justify-start mb-6">
              <button
                className="py-2 px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-bold rounded-lg shadow-md transition-all duration-300 hover:shadow-lg flex items-center"
                onClick={handleOpenDeposit}
                style={{ minWidth: 150 }}
              >
                <FiCreditCard className="mr-2" /> Deposit Here
              </button>
            </div>
            <div className="space-y-4">
              {tasks.length > 0 ? (
                tasks.map((task) => (
                  <motion.div
                    key={task.taskId}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: tasks.indexOf(task) * 0.1 }}
                    className="flex items-start p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 p-2 rounded-full mr-3">
                      <FiShoppingCart className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      {/* Admin Checkbox Status */}
                      {(task.purchase || task.sale) && (
                        <div className="flex items-center mb-2 space-x-2">
                          {task.purchase && (
                            <div className="flex items-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                              <FiCheckCircle className="mr-1 h-4 w-4" />
                              Purchase Completed
                            </div>
                          )}
                          {task.sale && (
                            <div className="flex items-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-sm font-medium">
                              <FiCheckCircle className="mr-1 h-4 w-4" />
                              Sale Completed
                            </div>
                          )}
                        </div>
                      )}
                      {/* Show task image if available */}
                      {task.imageUrl && (
                        <div className="mb-2">
                          <img
                            src={task.imageUrl}
                            alt="Task"
                            className="h-24 w-24 object-cover rounded border shadow cursor-pointer"
                            style={{ maxWidth: '100%', maxHeight: '100px' }}
                            onClick={() => openImageModal(task.imageUrl)}
                          />
                        </div>
                      )}
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200">
                        Task {task.taskId}: {task.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{task.description}</p>
                      {task.withdrawRequestId && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          Related Withdrawal Request ID: {task.withdrawRequestId}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        <span className="font-medium">Assigned At:</span>{' '}
                        {formatTimestamp(task.assignedAt)}
                      </p>
                      {task.startedAt && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          <span className="font-medium">Started At:</span>{' '}
                          {formatTimestamp(task.startedAt)}
                        </p>
                      )}
                      {task.completedAt && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          <span className="font-medium">Completed At:</span>{' '}
                          {formatTimestamp(task.completedAt)}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                            task.status === 'completed'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : task.status === 'working'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}
                        >
                          {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                        </span>
                        {task.purchase && (
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            ✓ Purchase
                          </span>
                        )}
                        {task.sale && (
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            ✓ Sale
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex space-x-2">
                      {task.status === 'pending' && (
                        <button
                          className="py-1.5 px-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white text-sm font-medium rounded-lg transition-all duration-300 hover:shadow-md flex items-center"
                          onClick={() => handleStartTask(task)}
                        >
                          <FiPlay className="mr-1 h-4 w-4" />
                          Start Task
                        </button>
                      )}
                      {task.status === 'working' && (
                        <button
                          className="py-1.5 px-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg transition-all duration-300 hover:shadow-md flex items-center"
                          onClick={() => handleCompleteTask(task)}
                        >
                          <FiCheckCircle className="mr-1 h-4 w-4" />
                          Complete Task
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center">No tasks assigned to you.</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deposit Modal */}
      {depositModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-xs sm:max-w-sm border-2 border-blue-300 dark:border-blue-700 mx-2"
          >
            <button
              onClick={handleCloseDeposit}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
              aria-label="Close deposit modal"
            >
              <FiX className="text-2xl" />
            </button>
            <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-4 flex items-center">
              <FiCreditCard className="mr-2" /> Deposit Amount
            </h3>
            {depositError && <div className="bg-red-100 text-red-700 p-2 rounded mb-2 text-xs">{depositError}</div>}
            {depositSuccess && <div className="bg-green-100 text-green-700 p-2 rounded mb-2 text-xs">{depositSuccess}</div>}
            {/* Step 1: Amount input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Amount</label>
              <input
                type="number"
                min="1"
                value={depositAmount}
                onChange={e => {
                  setDepositAmount(e.target.value);
                  setSelectedPayment('');
                  setShowPaymentNumber(false);
                  setDepositScreenshot(null);
                  setDepositScreenshotPreview('');
                }}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white"
                placeholder="Enter amount"
                disabled={depositLoading}
              />
            </div>
            {/* Step 2: Payment method options, only after valid amount */}
            {depositAmount && Number(depositAmount) > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Select Payment Method</label>
                <div className="flex flex-row gap-4 justify-center">
                  <label
                    className={`relative flex flex-col items-center p-4 border rounded-xl cursor-pointer transition-all duration-300 w-32 ${selectedPayment === 'gpay' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 hover:border-green-300 hover:shadow-sm'}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="gpay"
                      checked={selectedPayment === 'gpay'}
                      onChange={() => { setSelectedPayment('gpay'); setShowPaymentNumber(true); }}
                      className="absolute opacity-0"
                      disabled={depositLoading}
                    />
                    <img src={gpayImg} alt="Google Pay" className="h-12 w-12 mb-2" />
                    <span className="text-base font-semibold text-gray-800">GPay</span>
                  </label>
                  <label
                    className={`relative flex flex-col items-center p-4 border rounded-xl cursor-pointer transition-all duration-300 w-32 ${selectedPayment === 'phonepe' ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 hover:border-purple-300 hover:shadow-sm'}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="phonepe"
                      checked={selectedPayment === 'phonepe'}
                      onChange={() => { setSelectedPayment('phonepe'); setShowPaymentNumber(true); }}
                      className="absolute opacity-0"
                      disabled={depositLoading}
                    />
                    <img src={phonepeImg} alt="PhonePe" className="h-12 w-12 mb-2" />
                    <span className="text-base font-semibold text-gray-800">PhonePe</span>
                  </label>
                </div>
              </div>
            )}
            {/* Step 3: Show payment number and screenshot upload for selected method */}
            {showPaymentNumber && selectedPayment && (
              <div className="mb-4 text-center">
                <span className="block text-xs text-gray-500 mb-1">Pay to this number:</span>
                <span className="font-bold text-lg text-blue-700 dark:text-blue-300">{selectedPayment === 'gpay' ? paymentInfo.gpayNumber : paymentInfo.phonepeNumber}</span>
                {/* Message with amount and method */}
                <div className="mt-2 text-sm text-blue-800 dark:text-blue-200 font-medium">
                  Pay ₹{depositAmount} on {selectedPayment === 'gpay' ? 'GPay' : 'PhonePe'}
                </div>
                {/* Screenshot upload */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Upload Payment Screenshot</label>
                  <label className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition duration-200 flex items-center text-sm">
                    <FiUpload className="mr-2" />
                    Choose Screenshot
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleDepositScreenshotChange}
                      className="hidden"
                      disabled={depositLoading}
                    />
                  </label>
                  {depositScreenshotPreview && (
                    <div className="bg-gray-50 p-2 rounded-lg mt-2">
                      <img
                        src={depositScreenshotPreview}
                        alt="Deposit Screenshot Preview"
                        className="max-w-full h-32 object-contain rounded-lg shadow-sm border border-gray-200 mx-auto"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Step 4: Confirm button */}
            <button
              className="w-full py-2 px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-bold rounded-lg shadow-md transition-all duration-300 hover:shadow-lg flex items-center justify-center disabled:opacity-60"
              onClick={handleDepositSubmit}
              disabled={depositLoading || !depositAmount || !selectedPayment || !depositScreenshot}
            >
              {depositLoading ? 'Processing...' : 'Confirm Deposit'}
            </button>
          </motion.div>
        </div>
      )}

      <style jsx>{`
        @keyframes shine {
          0% {
            background-position: -200%;
          }
          100% {
            background-position: 200%;
          }
        }
        .animate-shine {
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          animation: shine 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default Task;